import { useEffect, useState, useRef } from "react";
import { RootState } from "../common/redux/store";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { deleteUser, getUserProfessionnel } from "../common/redux/userAction";
import { storageService } from "../common/service/storageService";
import {
    Alert,
    Box,
    Button,
    Grid,
    Paper,
    Stack,
    TextField,
    useTheme,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../common/redux/reduxHooks";
import { UserProfessionnelResponse } from "../professionnels/model/ProfessionnelModel";
import {
    getEmptyAvatarStructure,
    getPictureFromProfilImage,
} from "../utils/FormatUtils";
import {
    ProfessionnelProfileUpdate,
    professionnelUpdateProfil,
} from "./redux/profilAction";
import { logout } from "../login/redux/authAction";
import { Modal } from "../common/components/Modal";
import { Country } from "../common/redux/countryActions";
import CountrySelect from "../common/components/CountryInput";
import AdmnistrativeAreaSelect from "../common/components/AdmnistrativeAreaSelect";
import { RouteAPI } from "../common/service/apiService";
import { Profession } from "../common/redux/professionAction";
import ProfessionSelect from "../common/components/ProfessionInput";

export const ProfessionnelForm = () => {
    const userSelector: UserProfessionnelResponse = useAppSelector(
        (state: RootState) => state.user.proData,
    );
    const profilSelector: any = useAppSelector(
        (state: RootState) => state.profil,
    );
    const [account, setAccount] =
        useState<UserProfessionnelResponse>(userSelector);

    const [errorMessages, setErrorMessages] = useState<{
        [K in keyof ProfessionnelProfileUpdate]?: string;
    }>({});

    const [isOpenConfirmModal, setIsOpenConfirmModal] =
        useState<boolean>(false);
    const [isOpenConfirmModalDelete, setIsOpenConfirmModalDelete] =
        useState<boolean>(false);
    const [isOpenErrorModal, setIsOpenErrorModal] = useState<boolean>(false);

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const theme = useTheme();
    const modalRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (userSelector) {
            setAccount(userSelector);
        }
    }, [userSelector]);

    useEffect(() => {
        const idUser = storageService.getIdWithToken();
        if (idUser) dispatch(getUserProfessionnel(idUser));
    }, [dispatch]);

    useEffect(() => {
        const handleClose = (event: MouseEvent) => {
            if (
                modalRef.current &&
                !modalRef.current.contains(event.target as Node)
            ) {
                setIsOpenConfirmModalDelete(false);
            }
        };

        if (isOpenConfirmModalDelete) {
            document.addEventListener("mousedown", handleClose);
        } else {
            document.removeEventListener("mousedown", handleClose);
        }

        return () => {
            document.removeEventListener("mousedown", handleClose);
        };
    }, [isOpenConfirmModalDelete]);

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!profilSelector.error) {
            const data = new FormData(event.currentTarget);
            let validationMessages: typeof errorMessages = {};

            if (!data.get("nom"))
                validationMessages.nom = t("profileForm.validation.lastname");
            if (!data.get("prenom"))
                validationMessages.prenom = t(
                    "profileForm.validation.firstname",
                );
            if (!account.profession.length)
                validationMessages.profession = t(
                    "profileForm.validation.profession",
                );
            if (!account.country)
                validationMessages.country = t(
                    "profileForm.validation.country",
                );
            if (!account.administrativeAreas?.length)
                validationMessages.administrativeAreas = t(
                    "profileForm.validation.administrativeAreas",
                );

            if (Object.keys(validationMessages).length > 0) {
                setErrorMessages(validationMessages);
                return;
            }

            setErrorMessages({});

            dispatch(
                professionnelUpdateProfil({
                    nom: data.get("nom") as string,
                    prenom: data.get("prenom") as string,
                    description: data.get("description") as string,
                    profession: account.profession.map(
                        ({ id }) => `/api/professions/${id}`,
                    ),
                    administrativeAreas: account.administrativeAreas.map(
                        ({ id }) => `${RouteAPI.ADMINISTRATIVE_AREA}/${id}`,
                    ),
                    country: `${RouteAPI.COUNTRY}/${account.country.id}`,
                }),
            ).then((res: any) => {
                if (res.error) {
                    setIsOpenErrorModal(true);
                    return;
                }

                setIsOpenConfirmModal(true);
                dispatch(getUserProfessionnel(account.id));
            });
        }
    };

    const handleDeleteAccount = () => {
        dispatch(deleteUser()).then(() => {
            setIsOpenConfirmModalDelete(false);
            dispatch(logout());
            navigate("/login");
        });
    };

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                height: "100%",
                marginTop: "2rem",
            }}
        >
            <Paper
                sx={{
                    maxWidth: theme.breakpoints.values.md,
                    backgroundColor: "#F8F9FA",
                    height: "100%",
                    mr: "auto",
                    ml: "auto",
                    mt: 8,
                    mb: 3,
                    p: 5,
                    boxShadow:
                        "rgba(0, 0, 0, 0.4) 0px 2px 4px, rgba(0, 0, 0, 0.3) 0px 7px 13px -3px, rgba(0, 0, 0, 0.2) 0px -3px 0px inset",
                }}
                elevation={12}
            >
                <Box
                    sx={{
                        marginTop: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                    <Box
                        sx={{
                            maxWidth: "300px",
                            maxHeight: "300px",
                            mt: "0px",
                        }}
                    >
                        {account.profileImage && (
                            <img
                                src={getPictureFromProfilImage(
                                    account.profileImage.contentUrl,
                                )}
                                style={{
                                    maxWidth: "200px",
                                    maxHeight: "200px",
                                }}
                                alt="Profil"
                            />
                        )}
                        {!account.profileImage && (
                            <img
                                src={getEmptyAvatarStructure()}
                                style={{
                                    maxWidth: "200px",
                                    maxHeight: "200px",
                                }}
                                alt="Profil"
                            />
                        )}
                    </Box>
                    <Box
                        component="form"
                        noValidate
                        onSubmit={handleSubmit}
                        sx={{ mt: 3 }}
                    >
                        <Box
                            sx={{
                                mb: 4,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                flexDirection: "column",
                            }}
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        required
                                        fullWidth
                                        id="nom"
                                        label={t("sign.up.lastname")}
                                        name="nom"
                                        autoComplete="nom"
                                        defaultValue={account.nom}
                                        error={!!errorMessages.nom}
                                        helperText={errorMessages.nom}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        required
                                        fullWidth
                                        id="prenom"
                                        label={t("sign.up.firstname")}
                                        name="prenom"
                                        autoComplete="prenom"
                                        defaultValue={account.prenom}
                                        error={!!errorMessages.prenom}
                                        helperText={errorMessages.prenom}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <ProfessionSelect
                                        label={t("sign.up.profession")}
                                        value={account.profession}
                                        onChange={(
                                            newProfessions: Profession[],
                                        ) => {
                                            setAccount({
                                                ...account,
                                                profession: newProfessions,
                                            });
                                        }}
                                        multiple={true}
                                        required
                                        sx={{
                                            width: "100%",
                                            backgroundColor: "white",
                                            borderRadius: "5px",
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <CountrySelect
                                        defaultValue={account.country?.id}
                                        id="country"
                                        name="country"
                                        value={account.country}
                                        errorMessage={errorMessages.country}
                                        onChange={(newCountry: Country) => {
                                            setAccount({
                                                ...account,
                                                country: newCountry,
                                            });
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <AdmnistrativeAreaSelect
                                        country={account.country}
                                        value={account.administrativeAreas}
                                        multiple
                                        label={t(
                                            "profileForm.labels.addAdministrativeArea",
                                            {
                                                administrativeAreaName: (
                                                    account.country
                                                        .administrativeAreaName ??
                                                    t(
                                                        "inputLabels.administrativeArea",
                                                    )
                                                ).toLowerCase(),
                                            },
                                        )}
                                        sx={{
                                            width: "100%",
                                            backgroundColor: "white",
                                            borderRadius: "5px",
                                        }}
                                        errorMessage={
                                            errorMessages.administrativeAreas
                                        }
                                        onChange={(newAreas) => {
                                            setAccount({
                                                ...account,
                                                administrativeAreas:
                                                    newAreas ?? [],
                                            });
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        required
                                        fullWidth
                                        id="telephone"
                                        label={t("sign.up.phone")}
                                        name="telephone"
                                        autoComplete="tel-national"
                                        defaultValue={account.telephone}
                                        disabled
                                    />
                                    <Box sx={{ mt: 1 }}>
                                        <em>
                                            {t("profileForm.disabled.phone")}
                                        </em>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        margin="normal"
                                        required
                                        fullWidth
                                        id="email"
                                        label={t("auth.email")}
                                        name="email"
                                        autoComplete="email"
                                        defaultValue={account.email}
                                        disabled
                                    />
                                    <Box sx={{ mt: 1 }}>
                                        <em>
                                            {t("profileForm.disabled.email")}
                                        </em>
                                    </Box>
                                </Grid>
                            </Grid>

                            <BtnContainer>
                                <UpdateBtn type="submit" variant="contained">
                                    {t("button.update.profile")}
                                </UpdateBtn>
                                <DeleteBtn
                                    onClick={() =>
                                        setIsOpenConfirmModalDelete(true)
                                    }
                                    variant="contained"
                                >
                                    {t("button.delete.profile")}
                                </DeleteBtn>
                            </BtnContainer>
                        </Box>
                    </Box>

                    {profilSelector.error && (
                        <Alert severity="error" className="errorMessage">
                            {profilSelector.error?.response?.data.detail}
                        </Alert>
                    )}
                    <div ref={modalRef}>
                        <Modal
                            open={isOpenConfirmModalDelete}
                            onValidation={() => handleDeleteAccount()}
                            onClose={() => setIsOpenConfirmModalDelete(false)}
                            title={t("modal.delete.profile.title")}
                            message={t("modal.delete.profile.description")}
                            titleButtonValidation={t("button.delete.project")}
                        />
                    </div>
                    <Modal
                        open={isOpenConfirmModal}
                        onValidation={() => setIsOpenConfirmModal(false)}
                        onClose={() => setIsOpenConfirmModal(false)}
                        title={t("modal.update.profile.title")}
                        message={t("modal.update.profile.description")}
                        titleButtonValidation={t("button.modal.valid")}
                    />

                    <Modal
                        open={isOpenErrorModal}
                        onValidation={() => setIsOpenErrorModal(false)}
                        onClose={() => setIsOpenErrorModal(false)}
                        title={t("profileForm.errorModal.title")}
                        message={t("profileForm.errorModal.description")}
                        titleButtonValidation={t(
                            "profileForm.errorModal.button",
                        )}
                    />
                </Box>
            </Paper>
        </Box>
    );
};

const UpdateBtn = styled(Button)({
    width: "180px",
    padding: "0.5rem",
    borderRadius: "10px",
    backgroundImage:
        "linear-gradient(to bottom, #b5e550, #abd854, #a1cb58, #97be5a, #8eb15c)",
    textTransform: "uppercase",
    fontSize: "14px !important",
});

const DeleteBtn = styled(Button)({
    width: "180px",
    padding: "0.5rem",
    borderRadius: "10px",
    backgroundImage:
        "linear-gradient(to right bottom, #ff0000, #ff322a, #ff4c46, #ff625f, #ff7676)",
    textTransform: "uppercase",
    fontSize: "14px !important",
});

const BtnContainer = styled(Stack)({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    gap: "1rem",
    marginTop: "2rem",

    "@media(width >= 768px)": {
        flexDirection: "row",
        gap: "2rem",
    },
});
